import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  styled,
  Typography,
  alpha,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

import { FallbackProps } from "react-error-boundary";

import { useConfig } from "../../hooks/settingHooks";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { TextField } from "formik-mui";
import { useLocation } from "react-router-dom";
import { useQueryClient, useQueryErrorResetBoundary } from "react-query";
import { useNavigate } from "../../hooks/routingHooks";
import { UnauthorizedError } from "../../errors/UnauthorizedError";
import { NavigateToLogin } from "../redirects/NavigateToLogin";

interface Props extends FallbackProps {}

interface FormValues {
  config: string;
}

export function ConfigErrorFallback(props: Props) {
  const { resetErrorBoundary, error } = props;
  const queryClient = useQueryClient();
  const { reset } = useQueryErrorResetBoundary();

  const config = useConfig() || "";
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const initialValues: FormValues = { config };

  function parsePathname(
    pathname: string,
    oldConfig: string,
    newConfig: string
  ): string {
    if (pathname.indexOf(oldConfig) > -1)
      return location.pathname.replace(oldConfig, newConfig);
    return `/${newConfig}/app/layers`;
  }

  async function onSubmit(
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) {
    const { pathname } = location;
    const { resetForm } = helpers;
    if (config === values.config) {
      queryClient.invalidateQueries(["settings"]);
    }

    reset();
    resetErrorBoundary();
    resetForm({ values: values });
    return Promise.resolve(
      config !== values.config
        ? navigate(parsePathname(pathname, config, values.config))
        : -1
    );
  }

  return (
    <>
      {UnauthorizedError.isUnauthorizedError(error) && (
        <NavigateToLogin
          config={config}
          reset={() => {
            reset();
            resetErrorBoundary();
          }}
        />
      )}
      {!UnauthorizedError.isUnauthorizedError(error) && (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {() => (
            <Form>
              <StyledCard>
                <CardHeader
                  avatar={<Avatar>😕</Avatar>}
                  title={
                    <Typography variant="h4" component="h1">
                      {t("label.somethingWentWrong")}
                    </Typography>
                  }
                />
                <CardContent>
                  <Typography variant="body1" component="p">
                    {config && (
                      <Trans
                        i18nKey="message.couldNotLoadConfiguration"
                        tOptions={{ config }}
                        components={{ i: <i></i> }}
                      />
                    )}
                    {!config && (
                      <>{t("message.noConfigurationProvidedPleaseFillIn")}</>
                    )}
                  </Typography>
                  <Field
                    component={TextField}
                    name="config"
                    fullWidth
                    autoFocus
                    label={t("label.configurationName")}
                  />
                </CardContent>
                <CardActions>
                  <Button type="submit" color="primary">
                    {t("label.tryAgain")}
                  </Button>
                </CardActions>
              </StyledCard>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}

const StyledCard = styled(Card)`
  margin: ${({ theme }) => theme.spacing(3, "auto")};
  border: 1px solid ${({ theme }) => theme.palette.error.main};
  background-color: ${({ theme }) => alpha(theme.palette.error.main, 0.12)};

  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 90vw;
  }
  ${({ theme }) => theme.breakpoints.up("xs")} {
    max-width: 90vw;
  }

  .MuiFormControl-root {
    margin-top: ${({ theme }) => theme.spacing(3)};
  }
`;
